/**
 * Touch event utilities for swipe gesture detection
 * Detects swipe direction and threshold-based gestures
 */

/** Swipe direction constants */
export enum SwipeDirection {
  LEFT = 1,
  RIGHT = 2,
  UP = 3,
  DOWN = 4,
}

/**
 * Touch event analyzer for swipe gestures
 * Calculates swipe direction from touch start/end events
 */
export default class TouchEventAnalyzer {
  /** Minimum pixel distance to register as swipe */
  static readonly SWIPE_THRESHOLD = 50

  /** Swipe direction constants */
  static readonly SWIPE_LEFT = SwipeDirection.LEFT
  static readonly SWIPE_RIGHT = SwipeDirection.RIGHT
  static readonly SWIPE_UP = SwipeDirection.UP
  static readonly SWIPE_DOWN = SwipeDirection.DOWN

  private startEvent: TouchEvent
  private endEvent: TouchEvent | null

  /**
   * Create touch event analyzer
   * @param startEvent - Touch start event
   * @param endEvent - Touch end event (optional, can be set later)
   */
  constructor(startEvent: TouchEvent, endEvent?: TouchEvent | null) {
    this.startEvent = startEvent
    this.endEvent = endEvent ?? null
  }

  /**
   * Check if gesture is swipe left
   */
  isSwipeLeft(): boolean {
    return this.getSwipeDirection() === SwipeDirection.LEFT
  }

  /**
   * Check if gesture is swipe right
   */
  isSwipeRight(): boolean {
    return this.getSwipeDirection() === SwipeDirection.RIGHT
  }

  /**
   * Check if gesture is swipe up
   */
  isSwipeUp(): boolean {
    return this.getSwipeDirection() === SwipeDirection.UP
  }

  /**
   * Check if gesture is swipe down
   */
  isSwipeDown(): boolean {
    return this.getSwipeDirection() === SwipeDirection.DOWN
  }

  /**
   * Calculate swipe direction from touch events
   * @returns Swipe direction or null if no swipe detected
   */
  getSwipeDirection(): SwipeDirection | null {
    if (!this.endEvent) return null

    const start = this.startEvent.changedTouches[0]
    const end = this.endEvent.changedTouches[0]

    if (!start || !end) {
      return null
    }

    const horizontalDifference = start.screenX - end.screenX
    const verticalDifference = start.screenY - end.screenY

    // Horizontal difference dominates
    if (Math.abs(horizontalDifference) > Math.abs(verticalDifference)) {
      if (horizontalDifference >= TouchEventAnalyzer.SWIPE_THRESHOLD) {
        return SwipeDirection.LEFT
      } else if (horizontalDifference <= -TouchEventAnalyzer.SWIPE_THRESHOLD) {
        return SwipeDirection.RIGHT
      }

      // Vertical difference dominates
    } else {
      if (verticalDifference >= TouchEventAnalyzer.SWIPE_THRESHOLD) {
        return SwipeDirection.UP
      } else if (verticalDifference <= -TouchEventAnalyzer.SWIPE_THRESHOLD) {
        return SwipeDirection.DOWN
      }
    }

    return null
  }

  /**
   * Set the touch end event
   * @param endEvent - Touch end event
   */
  setEndEvent(endEvent: TouchEvent): void {
    this.endEvent = endEvent
  }
}